import { useEffect, useMemo, useState, useCallback, forwardRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { toast } from "react-toastify";
import {
  searchChargeCaptureServices,
  investigationCategoryToGroupCode,
} from "../../../ipd-billing/billing-api";
import {
  createLabOrderBatchId,
  rememberLabOrderBatch,
} from "../../../lab-technician/labOrdersShared";
import usePharmacyStore from "../../hooks/usePharmacyStore";
import PharmacyStoreSelect from "../PharmacyStoreSelect";
import useCarePlanServiceSearch, {
  getChargeSearchConfig,
  stripDoseForm,
} from "../../hooks/useCarePlanServiceSearch";
import CarePlanServiceAutocomplete from "../CarePlanServiceAutocomplete";
import { buildLabTestsFromEntries } from "../../utils/carePlanLabOrders";
import {
  postCarePlanBillingCharges,
  stripBillingMetaFromEntry,
  stripBillingMetaFromPayload,
  stripServiceMetaDeep,
} from "../../utils/carePlanBilling";
import { listOrderSets, expandOrderSet } from "../../api/orderSetsApi";
import { buildMedicationDetails, totalQuantity } from "../../../../utils/medicationOrder";

const toLabel = (value = "") =>
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

// Medication order's "instructions" field is a dropdown (not free text) using the
// same meal-timing options as the Investigation tab's test_items.instruction column.
const MEDICATION_INSTRUCTION_OPTIONS = [
  "Before Breakfast",
  "After Breakfast",
  "With Breakfast",
  "Before Lunch",
  "After Lunch",
  "With Lunch",
  "Before Dinner",
  "After Dinner",
  "With Dinner",
  "Before Food",
  "After Food",
  "With Food",
  "Empty Stomach",
  "Fasting",
  "Bedtime",
  "Morning",
  "Afternoon",
  "Evening",
  "Night",
  "As Needed",
  "Immediately",
  "Before Procedure",
  "After Procedure",
];

const getDefaultFieldValue = (field) => {
  if (field.type === "multi-select") return [];
  if (field.type === "table") return []; // start empty; user adds rows
  if (field.type === "boolean") return false;
  if (field.type === "number") return "";
  if (field.type === "object" || field.type === "group") {
    return buildInitialValues(field.structure || []);
  }
  return "";
};

const buildInitialValues = (fields = []) =>
  fields.reduce((acc, field) => {
    if (!field?.field) return acc;
    acc[field.field] = getDefaultFieldValue(field);
    return acc;
  }, {});

const getNestedValue = (source, path) =>
  path.reduce(
    (current, key) => (current && typeof current === "object" ? current[key] : undefined),
    source
  );

const updateNestedValue = (source, path, value) => {
  const cloned = Array.isArray(source) ? [...source] : { ...source };
  let cursor = cloned;

  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];
    const nextValue = cursor[key] && typeof cursor[key] === "object" ? { ...cursor[key] } : {};
    cursor[key] = nextValue;
    cursor = nextValue;
  }

  cursor[path[path.length - 1]] = value;
  return cloned;
};

const pad2 = (n) => String(n).padStart(2, "0");

/** Parse API / form string to Date for picker; returns null if empty/invalid */
const parseDateValue = (raw) => {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  const isoDate = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (isoDate) {
    const [, yyyy, mm, dd, hh, min] = isoDate;
    const d = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      hh != null ? Number(hh) : 0,
      min != null ? Number(min) : 0,
      0,
      0
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (s.includes("T")) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (m) {
    const [, dd, mm, yyyy, hh, min] = m;
    const d = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      hh != null ? Number(hh) : 0,
      min != null ? Number(min) : 0,
      0,
      0
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d2 = new Date(s);
  return Number.isNaN(d2.getTime()) ? null : d2;
};

const formatDateForApi = (date, includeTime) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  if (includeTime) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
      date.getHours()
    )}:${pad2(date.getMinutes())}:00`;
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const formatTimeForApi = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "number") return Number.isNaN(value) || value === "";
  if (typeof value === "boolean") return value === false;
  if (Array.isArray(value)) return value.every((item) => isEmptyValue(item));
  if (typeof value === "object") {
    return Object.values(value).every((nestedValue) => isEmptyValue(nestedValue));
  }
  return false;
};

// [VALIDATION] Mirrors care_plan_structures "required": true — used on Add To Activity + Save.
const isFieldRequired = (field) => field?.required === true;

const isRequiredValueMissing = (value, field) => {
  if (!isFieldRequired(field)) return false;
  if (field.type === "boolean") return value !== true;
  if (field.type === "multi-select") return !Array.isArray(value) || value.length === 0;
  if (field.type === "number") {
    return value === "" || value === null || value === undefined || Number.isNaN(Number(value));
  }
  return isEmptyValue(value);
};

const collectRequiredFieldDefs = (structureFields = [], pathPrefix = []) => {
  const requiredFields = [];
  structureFields.forEach((field) => {
    if (!field?.field) return;
    const path = [...pathPrefix, field.field];
    if (field.type === "object") {
      requiredFields.push(...collectRequiredFieldDefs(field.structure || [], path));
      return;
    }
    if (isFieldRequired(field)) {
      requiredFields.push({ field, path, label: toLabel(field.field) });
    }
  });
  return requiredFields;
};

const validateEntryRequiredFields = (entry, structureFields = []) => {
  const errors = {};
  collectRequiredFieldDefs(structureFields).forEach(({ field, path, label }) => {
    const value = getNestedValue(entry, path);
    if (isRequiredValueMissing(value, field)) {
      errors[path.join(".")] = `${label} is required`;
    }
  });
  return errors;
};

const getEntryDisplayLabel = (entry, activityNameRaw = "") => {
  const activityName = String(activityNameRaw || "").toLowerCase().trim();

  if (activityName === "medication") {
    const med = entry?.medication || {};
    const name = String(med?.drug_name || med?.name || "").trim();
    const frequency =
      med?.frequency !== null && med?.frequency !== undefined && String(med.frequency).trim() !== ""
        ? String(med.frequency).trim()
        : "";

    if (name && frequency) return `${name} • Frequency: ${frequency}`;
    if (name) return `${name} • Frequency: -`;
    if (frequency) return `Medication • Frequency: ${frequency}`;
    return "Medication • Frequency: -";
  }

  if (activityName === "task") {
    const taskName = String(entry?.task || entry?.title || entry?.name || "").trim();
    const frequency = String(entry?.frequency || "").trim();
    if (taskName && frequency) return `${taskName} • Frequency: ${frequency}`;
    if (taskName) return `${taskName} • Frequency: -`;
    if (frequency) return `Task • Frequency: ${frequency}`;
    return "Task • Frequency: -";
  }

  if (activityName === "test") {
    const testName = String(entry?.testName || entry?.orderDetail || entry?.name || "").trim();
    const priority = String(entry?.priority || "").trim();
    if (testName && priority) return `${testName} • Priority: ${priority}`;
    if (testName) return `${testName} • Priority: -`;
    if (priority) return `Test • Priority: ${priority}`;
    return "Test • Priority: -";
  }

  return `${toLabel(activityNameRaw || "Entry")}`;
};

const displayOrDash = (value) => {
  if (value === null || value === undefined) return "-";
  const text = String(value).trim();
  return text ? text : "-";
};

const getTableConfigForActivity = (activityNameRaw = "") => {
  const activityName = String(activityNameRaw || "").toLowerCase().trim();

  if (activityName === "medication") {
    return {
      columns: [
        { key: "name", label: "Medicine Name" },
        { key: "frequency", label: "Frequency" },
        { key: "duration", label: "Duration" },
      ],
      getRow: (entry) => {
        const med = entry?.medication || {};
        const dur = displayOrDash(med?.duration);
        const durUnit = displayOrDash(med?.duration_unit);
        return {
          name: displayOrDash(med?.drug_name || med?.name),
          frequency: displayOrDash(med?.frequency),
          duration:
            dur === "-" && durUnit === "-"
              ? "-"
              : `${dur !== "-" ? dur : ""}${durUnit !== "-" ? ` ${durUnit}` : ""}`.trim(),
        };
      },
    };
  }

  if (activityName === "task") {
    return {
      columns: [
        { key: "taskNo", label: "Task No." },
        { key: "frequency", label: "Frequency" },
        { key: "priority", label: "Priority" },
        { key: "notes", label: "Notes" },
      ],
      getRow: (entry, index) => ({
        taskNo: String(index + 1),
        frequency: displayOrDash(entry?.frequency),
        priority: displayOrDash(entry?.priority),
        notes: displayOrDash(entry?.notes),
      }),
    };
  }

  if (activityName === "test") {
    return {
      columns: [
        { key: "testName", label: "Test Name" },
        { key: "priority", label: "Priority" },
        { key: "frequency", label: "Frequency" },
        { key: "scheduledDateTime", label: "Scheduled" },
      ],
      getRow: (entry) => ({
        testName: displayOrDash(entry?.testName || entry?.orderDetail || entry?.name),
        priority: displayOrDash(entry?.priority),
        frequency: displayOrDash(entry?.frequency),
        scheduledDateTime: displayOrDash(entry?.scheduledDateTime || entry?.occurrenceDateTime),
      }),
    };
  }

  return null;
};

/** HH:mm stored for time-only fields */
const parseTimeFieldValue = (raw) => {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  const hm = s.match(/^(\d{1,2}):(\d{2})$/);
  if (hm) {
    const d = new Date(2000, 0, 1, Number(hm[1]), Number(hm[2]), 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(`2000-01-01T${s}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const DatePickerTextField = forwardRef(
  ({ label, value, onClick, disabled, endIcon, ...rest }, ref) => {
    const { InputProps: restInputProps = {}, inputProps: restFieldInputProps, sx: restSx, ...tfRest } =
      rest;
    return (
      <TextField
        {...tfRest}
        inputRef={ref}
        fullWidth
        size="small"
        label={label}
        value={value || ""}
        onClick={onClick}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        inputProps={{ readOnly: true, ...restFieldInputProps }}
        InputProps={{
          ...restInputProps,
          endAdornment: (
            <InputAdornment
              position="end"
              onClick={(e) => {
                if (disabled) return;
                e.stopPropagation();
                onClick?.(e);
              }}
              onMouseDown={(e) => {
                if (disabled) return;
                e.preventDefault();
              }}
              sx={{
                color: "action.active",
                ml: 0,
                cursor: disabled ? "default" : "pointer",
              }}
            >
              {endIcon}
            </InputAdornment>
          ),
        }}
        sx={[{ cursor: disabled ? "default" : "pointer" }, restSx].filter(Boolean)}
      />
    );
  }
);
DatePickerTextField.displayName = "DatePickerTextField";

const DateTimeFieldEndIcon = () => (
  <Box
    component="span"
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
    aria-hidden
  >
    <CalendarTodayIcon sx={{ fontSize: 18 }} />
    <AccessTimeIcon sx={{ fontSize: 18 }} />
  </Box>
);

const CARE_PLAN_DATE_PICKER_POPPER = {
  strategy: "fixed",
  modifiers: [
    {
      name: "zIndex",
      enabled: true,
      phase: "write",
      fn({ state }) {
        state.styles.popper.zIndex = 99999;
      },
    },
    { name: "preventOverflow", options: { padding: 8 } },
  ],
};

/*
 * ── CHARGE MASTER + BILLING FLOW (CarePlanActivityModal) ─────────────────────
 *
 * Grep for "[BILLING]" in this file to find every integration point.
 *
 * Flow overview:
 * 1. getChargeSearchConfig     — decides which fields get charge-master autocomplete
 * 2. fetchChargeMasterOptions  — GET search_charge_master/ for Test, search_medicines/
 *                                 for Medication (debounced in step 3)
 * 3. scheduleChargeMasterSearch — 1000ms debounce before calling step 2
 * 4. renderField Autocomplete    — Medication "name" / Test "orderDetail" UI
 * 5. setBillingChargeSelection   — stores service_code on entry as __billing_charge
 *                                  (Medication results have no service_code, so
 *                                  medication entries no longer populate this)
 * 6. handleAddToActivity         — __billing_charge travels with each queued entry
 * 7. handleSubmit                — strips __billing_charge for care_plans API, then
 *                                  calls postBillingCharges after onSubmit succeeds
 * 8. postBillingCharges          — single: billing/charges/ | multi: bulk-post/
 *
 * Parent wiring: PatientDetailsPanel passes admissionId prop (required for billing).
 * To disable billing entirely, remove the postBillingCharges call in handleSubmit.
 * To change which fields search, edit getChargeSearchConfig.
 * To change API endpoints/payloads, edit fetchChargeMasterOptions + postBillingCharges.
 * billing/charges/ payout extras (buildSingleBillingChargePayload):
 *   - Both cases → source_module: "IPD"
 *   - Referred To Me → referred_by_provider_id, payout_eligible, calculate_doctor_payout
 *   - Admitting list → calculate_doctor_payout only
 * Parent passes isReferredToMePatient + referredByProviderId from referred_to_me response.
 *
 * ── LAB TEST FLOW (Test activity only) ───────────────────────────────────────
 * Grep for "[LAB_TEST]" in this file to find every integration point.
 *
 * Flow overview:
 * 1. PatientDetailsPanel passes careContextId + doctorName from ipd_admission list
 *    (admission_details.care_context_ref, doctor_name — no extra admission fetch).
 * 2. setBillingChargeSelection stores charge-master fields on __billing_charge for lab payload.
 * 3. buildLabTestsFromEntries — maps __billing_charge → add_patient_lab_tests test[] items.
 * 4. postPatientLabTests — POST add_patient_lab_tests/ after care plan save
 *    (standalone Test activity + Orders → Investigation tab).
 *
 * To disable lab test posting, remove the postPatientLabTests call in handleSubmit.
 * To change payload fields, edit postPatientLabTests.
 *
 * ── PHARMACY PRESCRIPTION FLOW (Medication activity + Orders → Medication) ───
 * Grep for "[PHARMACY_RX]" in this file to find every integration point.
 *
 * Flow overview:
 * 1. PatientDetailsPanel passes patientId + provisionalDiagnosis + finalDiagnosis +
 *    allergies from the same normalized patient object OrderManagementDrawer uses.
 * 2. buildMedicinesFromEntries — maps queued medication entries to add_ipd_prescription/'s
 *    medicines[] shape (name/dose/frequency/duration), same as OrderManagementDrawer's
 *    Sign & Send cart mapping.
 * 3. postIpdPrescription — POST add_ipd_prescription/ after care plan save succeeds
 *    (standalone Medication activity + Orders → Medication tab), so the prescription
 *    reaches the pharmacy the same way it does from the Pharmacy tab's Sign & Send, and
 *    later appears in Order Management's Orders tab via get_ipd_prescriptions_by_admission/.
 *
 * To disable prescription posting, remove the postIpdPrescription call in handleSubmit.
 */

const getOrderSetId = (s) => s?.id ?? s?.order_set_id ?? s?.set_id;

const pickFieldOption = (options, preferred, fallback) => {
  const list = Array.isArray(options) ? options : [];
  const match = (want) => {
    if (want == null || String(want).trim() === "") return undefined;
    const exact = list.find((o) => String(o) === String(want));
    if (exact !== undefined) return exact;
    const lower = String(want).toLowerCase();
    return list.find((o) => String(o).toLowerCase() === lower);
  };
  return match(preferred) ?? match(fallback) ?? list[0] ?? preferred ?? fallback ?? "";
};

const defaultRouteForForm = (form = "") => {
  const map = {
    Tablet: "Oral",
    Capsule: "Oral",
    Injection: "IV",
    Syrup: "Oral",
    Inhaler: "Inhalation",
    Gel: "Topical",
    Drops: "Ophthalmic",
    Cream: "Topical",
  };
  return map[form] || "Oral";
};


// [BILLING] admissionId prop is required for billing API calls (passed from PatientDetailsPanel).
// [LAB_TEST] careContextId + doctorName come from ipd_admission list via PatientDetailsPanel.
// [PHARMACY_RX] patientId + provisionalDiagnosis + finalDiagnosis + allergies come from the
// same normalized patient object via PatientDetailsPanel — needed for add_ipd_prescription/.
const CarePlanActivityModal = ({
  open,
  onClose,
  structureDef,
  onSubmit,
  admissionId,
  careContextId,
  doctorName,
  isReferredToMePatient = false,
  referredByProviderId = "",
  patientId = "",
  provisionalDiagnosis = "",
  finalDiagnosis = "",
  allergies = [],
}) => {
  const fields = useMemo(() => structureDef?.structure || [], [structureDef]);
  const activityName = String(structureDef?.name || "")
    .toLowerCase()
    .trim();
  const isMultiEntry =
    structureDef?.multi_entry === true ||
    structureDef?.multiEntry === true ||
    Number(structureDef?.multi_entry) === 1 ||
    Number(structureDef?.multiEntry) === 1 ||
    activityName === "medication" ||
    activityName === "task" ||
    activityName === "test";
  const createEmptyEntry = useMemo(() => () => buildInitialValues(fields), [fields]);
  const ORDER_TABS = useMemo(() => [
    {
      label: "Medication",
      groupField: "medication_order",
      title: "Medication Order",
      subtitle: "Order medications and prescriptions",
      icon: <LocalPharmacyOutlinedIcon sx={{ fontSize: "1.4rem", color: "#6366f1" }} />,
      tabIcon: <LocalPharmacyOutlinedIcon />,
    },
    {
      label: "Investigation",
      groupField: "test_order",
      title: "Investigation Order",
      subtitle: "Order labs, imaging and diagnostics",
      icon: <BiotechOutlinedIcon sx={{ fontSize: "1.4rem", color: "#0ea5e9" }} />,
      tabIcon: <BiotechOutlinedIcon />,
    },
    {
      label: "Nutrition",
      groupField: "nutrition_order",
      title: "Nutrition Order",
      subtitle: "Order dietary and nutrition plans",
      icon: <RestaurantMenuIcon sx={{ fontSize: "1.4rem", color: "#22c55e" }} />,
      tabIcon: <RestaurantMenuIcon />,
    },
  ], []);

  const [formData, setFormData] = useState({});
  const [activeOrderTab, setActiveOrderTab] = useState(0);
  const [orderQueues, setOrderQueues] = useState({ medication_order: [], test_order: [], nutrition_order: [] });
  const [queuedEntries, setQueuedEntries] = useState([]);
  const [editingEntryIndex, setEditingEntryIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const activityTableConfig = useMemo(
    () => getTableConfigForActivity(structureDef?.name),
    [structureDef?.name]
  );

  const isAppointmentActivity = activityName === "appointment";
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  // Previous investigation / nutrition orders fetched from care_plan_data for quick reuse
  const [prevTests, setPrevTests] = useState([]);
  const [prevNutrition, setPrevNutrition] = useState([]);
  const [prevMedLoading, setPrevMedLoading] = useState(false);
  const [prevOrdersFetched, setPrevOrdersFetched] = useState(false);
  // Order Set picker — same list/expand APIs as Order Management's Pharmacy tab.
  const [orderSets, setOrderSets] = useState([]);
  const [orderSetsStatus, setOrderSetsStatus] = useState("idle");
  const [orderSetsError, setOrderSetsError] = useState("");
  const [selectedOrderSetId, setSelectedOrderSetId] = useState("");
  const [orderSetExpanding, setOrderSetExpanding] = useState(false);
  // [PHARMACY_RX] The pharmacy store every prescribe/search below is scoped to.
  // Auto-selected when the hospital has one; otherwise the picker rendered next
  // to "Order Set" in the Medication tab decides it.
  const pharmacyStore = usePharmacyStore();
  const pharmacyStoreId = pharmacyStore.storeId;

  const getApiHeaders = useCallback(() => {
    const authToken = sessionStorage.getItem("accessToken") || "";
    return {
      Authorization: `Bearer ${authToken}`,
      "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
      "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
      "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    };
  }, []);

  // [BILLING][PHARMACY_RX] Catalogue lookup + stock engine, shared with the voice
  // review screen so a dictated order searches, badges and bills identically.
  const serviceSearch = useCarePlanServiceSearch({ getApiHeaders, pharmacyStoreId });
  const {
    chargeOptionsByKey,
    chargeLoadingByKey,
    medStockByKey,
    setChargeOptionsByKey,
    setChargeLoadingByKey,
  } = serviceSearch;

  useEffect(() => {
    if (open && isAppointmentActivity) {
      setLoadingDepartments(true);
      axios
        .get(
          `${process.env.REACT_APP_BACKEND_ROUTE_URL}get_all_medical_departments/`,
          { headers: getApiHeaders() }
        )
        .then((res) => {
          if (res.data && res.data.department_list) {
            const departments = res.data.department_list.map((dep) => {
              if (typeof dep === "string") return { departmentName: dep.trim(), departmentId: "" };
              const departmentName = dep?.department || dep?.name || dep?.department_name || "";
              return {
                departmentName: String(departmentName || "").trim(),
                departmentId: String(dep?.department_id || dep?.departmentId || dep?.id || "").trim(),
              };
            }).filter((d) => d.departmentName);
            setDepartmentOptions(departments);
          }
        })
        .catch((err) => console.error("Error fetching departments:", err))
        .finally(() => setLoadingDepartments(false));
    }
    if (!open) {
      setDepartmentOptions([]);
      setDoctorOptions([]);
    }
  }, [open, isAppointmentActivity, getApiHeaders]);

  const fetchDoctorsByDepartment = useCallback(
    (departmentName) => {
      if (!departmentName) {
        setDoctorOptions([]);
        return;
      }
      setLoadingDoctors(true);
      axios
        .post(
          `${process.env.REACT_APP_BACKEND_ROUTE_URL}get_all_doctors_based_on_department/`,
          { department: departmentName },
          { headers: getApiHeaders() }
        )
        .then((res) => {
          if (res.data && res.data.response_list) {
            setDoctorOptions(
              res.data.response_list.map((doc) => ({
                doctor_id: doc.doctor_id,
                name: doc.name,
              }))
            );
          } else {
            setDoctorOptions([]);
          }
        })
        .catch((err) => {
          console.error("Error fetching doctors:", err);
          setDoctorOptions([]);
        })
        .finally(() => setLoadingDoctors(false));
    },
    [getApiHeaders]
  );

  useEffect(() => {
    if (open) {
      setFormData(createEmptyEntry());
      setQueuedEntries([]);
      setOrderQueues({ medication_order: [], test_order: [], nutrition_order: [] });
      setEditingEntryIndex(null);
      setFieldErrors({});
      setActiveOrderTab(0);
      // [BILLING] Reset charge-master search cache when modal opens/closes.
      setChargeOptionsByKey({});
      setChargeLoadingByKey({});
      // Reset prev orders so fresh data is fetched next open
      setPrevTests([]);
      setPrevNutrition([]);
      setPrevOrdersFetched(false);
      setSelectedOrderSetId("");
      setOrderSetExpanding(false);
    }
  }, [open, isMultiEntry, createEmptyEntry, setChargeOptionsByKey, setChargeLoadingByKey]);

  useEffect(() => {
    if (!open || activityName !== "orders") return;
    let cancelled = false;
    setOrderSetsStatus("loading");
    setOrderSetsError("");
    listOrderSets()
      .then((data) => {
        if (cancelled) return;
        setOrderSets(Array.isArray(data) ? data : []);
        setOrderSetsStatus("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        setOrderSets([]);
        setOrderSetsError(err.message || "Could not load order sets.");
        setOrderSetsStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [open, activityName]);

  // Auto-calculate total_quantity from MAEN + duration + unit.
  // Writes "" rather than bailing out when the regimen has no computable total:
  // returning early left the previous order's quantity on screen after the
  // duration was cleared, and submit_care_plan trusts this number.
  // TWO SHAPES, and the quantity must be written into the one the backend reads
  // for this template (manage_care_plan.medication_payload_for_entry):
  //   template 9 "orders"     -> cp_data.medication_order.medication
  //   template 3 "medication" -> cp_data.medication          (top level)
  // Handling only "orders" meant a medication order placed on the direct
  // Medication template submitted with no quantity at all, so
  // Prescription.details.total_quantity stayed unset and medication_returnable/
  // reported the ward stock as unknown.
  useEffect(() => {
    const isOrders = activityName === "orders";
    const isMedication = activityName === "medication";
    if (!isOrders && !isMedication) return;
    const med = (isOrders ? formData?.medication_order?.medication : formData?.medication) || {};
    const quantity = totalQuantity(med);
    const totalQty = quantity === null ? "" : quantity;
    setFormData((prev) => {
      const prevMed = isOrders ? prev?.medication_order?.medication : prev?.medication;
      if (String(prevMed?.total_quantity ?? "") === String(totalQty)) return prev;
      if (isOrders) {
        return {
          ...prev,
          medication_order: {
            ...(prev.medication_order || {}),
            medication: { ...(prev.medication_order?.medication || {}), total_quantity: totalQty },
          },
        };
      }
      return {
        ...prev,
        medication: { ...(prev.medication || {}), total_quantity: totalQty },
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activityName,
    formData?.medication_order?.medication?.frequency,
    formData?.medication_order?.medication?.morning,
    formData?.medication_order?.medication?.afternoon,
    formData?.medication_order?.medication?.evening,
    formData?.medication_order?.medication?.night,
    formData?.medication_order?.medication?.duration,
    formData?.medication_order?.medication?.duration_unit,
    formData?.medication?.frequency,
    formData?.medication?.morning,
    formData?.medication?.afternoon,
    formData?.medication?.evening,
    formData?.medication?.night,
    formData?.medication?.duration,
    formData?.medication?.duration_unit,
  ]);

  // Lazy-fetch previous orders — called only when doctor opens any reuse dropdown
  const fetchPrevMedications = useCallback(() => {
    if (prevMedLoading || prevOrdersFetched) return;
    setPrevMedLoading(true);
    const carePlanId = Number(structureDef?.care_plan_id || structureDef?.id || structureDef?.template_id || 0);
    axios
      .post(`${process.env.REACT_APP_BACKEND_ROUTE_URL}care_plan_data/`, { care_plan_id: carePlanId, admission_id: admissionId }, { headers: getApiHeaders() })
      .then((r) => {
        const raw = Array.isArray(r.data?.data) ? r.data.data
          : Array.isArray(r.data?.results) ? r.data.results
          : Array.isArray(r.data) ? r.data
          : [];

        const tests = [];
        const nutritions = [];
        const seenTests = new Set();

        raw.forEach((item) => {
          // Data is nested inside item.data; handle both plural (old) and singular (new) keys
          const orderData = item?.data || item;

          // ── Tests / Investigations ──
          const testOrders = [
            ...(Array.isArray(orderData?.test_orders) ? orderData.test_orders : []),
            ...(Array.isArray(orderData?.test_order) ? orderData.test_order : []),
          ];
          testOrders.forEach((testOrder) => {
            if (!testOrder || typeof testOrder !== "object") return;
            const items = Array.isArray(testOrder?.test_items) ? testOrder.test_items : [];
            const label = items.map((i) => i?.test_name || "").filter(Boolean).join(", ") ||
              testOrder?.category || "";
            if (!label || seenTests.has(label)) return;
            seenTests.add(label);
            tests.push({ label, data: testOrder });
          });

          // ── Nutrition ──
          const nutritionOrders = [
            ...(Array.isArray(orderData?.nutrition_orders) ? orderData.nutrition_orders : []),
            ...(Array.isArray(orderData?.nutrition_order) ? orderData.nutrition_order : []),
          ];
          nutritionOrders.forEach((nutritionOrder) => {
            if (!nutritionOrder || typeof nutritionOrder !== "object") return;
            const label = nutritionOrder?.oral_diet?.diet_type || nutritionOrder?.priority || "Nutrition order";
            nutritions.push({ label, data: nutritionOrder });
          });
        });

        setPrevTests(tests);
        setPrevNutrition(nutritions);
        setPrevOrdersFetched(true);
      })
      .catch(() => {
        setPrevTests([]);
        setPrevNutrition([]);
      })
      .finally(() => setPrevMedLoading(false));
  }, [getApiHeaders, prevMedLoading, prevOrdersFetched, structureDef, admissionId]);

  // [LAB_TEST] POST add_patient_lab_tests/ after care plan save succeeds.
  // Fires for the standalone "test" activity and for investigations ordered
  // via Orders → Investigation so they appear on the lab dashboard.
  const postPatientLabTests = useCallback(
    async (entries) => {
      if (activityName !== "test" && activityName !== "orders") return;

      const tests = buildLabTestsFromEntries(entries);
      if (tests.length === 0) {
        console.warn(
          "[LAB_TEST] Skipped add_patient_lab_tests — no investigations with test metadata.",
        );
        return;
      }

      if (!careContextId) {
        console.warn("[LAB_TEST] Skipped add_patient_lab_tests — missing care_context_id.");
        toast.warn(
          "Care plan saved, but lab order was not created (missing care context).",
        );
        return;
      }
      if (!admissionId) {
        console.warn("[LAB_TEST] Skipped add_patient_lab_tests — missing admission id.");
        return;
      }

      const resolvedDoctorName =
        doctorName ||
        sessionStorage.getItem("doctorName") ||
        sessionStorage.getItem("userName") ||
        "";

      // One Care Plan save → one lab batch on the dashboard. A later save for
      // the same patient gets a new id and shows as its own row.
      const labTestBatchId = createLabOrderBatchId();
      const testPayload = tests.map((t) => ({
        ...t,
        lab_test_batch_id: labTestBatchId,
      }));

      await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}add_patient_lab_tests/`,
        {
          care_context_id: careContextId,
          admission_id: String(admissionId),
          patient_type: "IPD",
          // Doctor-ordered investigations enter the lab dashboard as NEW.
          status: activityName === "orders" ? "NEW" : "PAID",
          doctor_name: resolvedDoctorName,
          lab_test_batch_id: labTestBatchId,
          // Order-level, alongside status — a priority sent only inside test[] came back
          // empty on the lab dashboard, and status (the other order-wide attribute) lives
          // here too. The form collects one priority per order, so this loses nothing.
          priority: testPayload[0]?.priority || "Routine",
          test: testPayload,
        },
        { headers: getApiHeaders() }
      );
      rememberLabOrderBatch(admissionId, labTestBatchId, testPayload);
    },
    [activityName, admissionId, careContextId, doctorName, getApiHeaders]
  );

  // [PHARMACY_RX] Maps queued medication entries to add_ipd_prescription/'s medicines[]
  // shape — { medicine_name, generic_medicine_names, details: { dose, frequency, duration } },
  // same M/A/E/N dose-summary convention OrderManagementDrawer's cart uses, so a
  // prescription looks the same whether it was sent from Pharmacy or from Care Plan Orders.
  // A flat { name, dose, ... } medicine is rejected by the API.
  const buildMedicinesFromEntries = useCallback(
    (entries) => {
      const medicines = [];
      // The generic is only ever resolved by the stock lookup (search_medicines/,
      // which drives the typeahead, has no generic). Recover it by drug name;
      // when the lookup never ran or came back empty, send the brand stripped of
      // its dose/form rather than an empty generic.
      const genericFor = (name) => {
        const lower = name.toLowerCase();
        const hit = Object.values(medStockByKey).find(
          (s) => String(s?.drugName || "").toLowerCase() === lower,
        );
        return hit?.genericName || stripDoseForm(name);
      };
      const formatDoseSummary = (med) =>
        [
          ["morning", "M"],
          ["afternoon", "A"],
          ["evening", "E"],
          ["night", "N"],
        ]
          .map(([key, label]) => {
            const value = parseFloat(med?.[key]) || 0;
            return value > 0 ? `${label}:${value}` : null;
          })
          .filter(Boolean)
          .join(" ");

      const pushMedicine = (med) => {
        const name = String(med?.drug_name || med?.name || "").trim();
        if (!name) return;
        // details is stored verbatim and handed straight to
        // generate_medication_schedule, so duration MUST go over numeric with
        // duration_unit beside it. The "5 Day(s)" string this used to send
        // failed float() server-side and scheduled every order for one day at
        // dose 1, whatever the doctor entered. dose keeps riding along for the
        // pharmacy screen, which reads it for display.
        medicines.push({
          medicine_name: name,
          generic_medicine_names: genericFor(name),
          details: buildMedicationDetails({ ...med, dose: formatDoseSummary(med) }),
        });
      };

      if (activityName === "medication") {
        entries.forEach((entry) => pushMedicine(entry?.medication));
      } else if (activityName === "orders") {
        entries.forEach((entry) => {
          (entry?.medication_order || []).forEach((row) => pushMedicine(row?.medication));
        });
      }
      return medicines;
    },
    [activityName, medStockByKey]
  );

  // [PHARMACY_RX] POST add_ipd_prescription/ after care plan save succeeds — fires for the
  // standalone Medication activity and for Orders → Medication so those medications reach
  // the pharmacy the same way Pharmacy tab's Sign & Send does.
  const postIpdPrescription = useCallback(
    async (entries) => {
      if (activityName !== "medication" && activityName !== "orders") return;

      const medicines = buildMedicinesFromEntries(entries);
      if (medicines.length === 0) return;

      if (!patientId) {
        console.warn("[PHARMACY_RX] Skipped add_ipd_prescription — missing patient id.");
        return;
      }

      await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}add_ipd_prescription/`,
        {
          patient_id: patientId,
          ipd_flag: true,
          provisional_diagnosis: provisionalDiagnosis || "",
          final_diagnosis: finalDiagnosis || "",
          status: "Sent",
          allergies: allergies || [],
          medicines,
        },
        { headers: getApiHeaders() }
      );
    },
    [activityName, buildMedicinesFromEntries, patientId, provisionalDiagnosis, finalDiagnosis, allergies, getApiHeaders]
  );

  // [BILLING] Posts charges after care plan save — shared with the voice review
  // screen so a dictated order bills on identical terms.
  const postBillingCharges = useCallback(
    (entries) =>
      postCarePlanBillingCharges({
        entries,
        admissionId,
        activityName,
        isReferredToMePatient,
        referredByProviderId,
        headers: getApiHeaders(),
      }),
    [admissionId, activityName, getApiHeaders, isReferredToMePatient, referredByProviderId]
  );

  const handleChange = (path, value) => {
    setFormData((prev) => updateNestedValue(prev, path, value));
    const pathKey = path.join(".");
    setFieldErrors((prev) => {
      if (!prev[pathKey]) return prev;
      const next = { ...prev };
      delete next[pathKey];
      return next;
    });
  };

  const applyFieldErrors = (errors) => {
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // [BILLING] Queued entries retain __billing_charge; bulk billing uses all queued on Save.
  const handleAddToActivity = () => {
    setQueuedEntries((prev) => {
      if (editingEntryIndex === null) {
        return [...prev, formData];
      }
      return prev.map((entry, idx) => (idx === editingEntryIndex ? formData : entry));
    });
    setEditingEntryIndex(null);
    setFormData(createEmptyEntry());
    setFieldErrors({});
  };

  const isOrderGroupEmpty = (data) => {
    if (!data || typeof data !== "object") return true;
    return Object.values(data).every((v) => {
      if (v === "" || v === null || v === undefined || v === false) return true;
      if (Array.isArray(v)) return v.length === 0;
      if (typeof v === "object") return isOrderGroupEmpty(v);
      return false;
    });
  };

  const getOrderEntrySummary = (groupField, entry) => {
    if (groupField === "medication_order") {
      return { cols: [
        { label: "Drug Name", value: entry?.medication?.drug_name || entry?.medication?.name || "—" },
        { label: "Route", value: entry?.medication?.route || "—" },
        { label: "Frequency", value: entry?.medication?.frequency || "—" },
        { label: "Priority", value: entry?.priority || "—" },
      ]};
    }
    if (groupField === "test_order") {
      const testCount = Array.isArray(entry?.test_items) ? entry.test_items.length : 0;
      return { cols: [
        { label: "Category", value: toLabel(entry?.category || "—") },
        { label: "Tests", value: testCount > 0 ? `${testCount} test${testCount !== 1 ? "s" : ""}` : "—" },
        { label: "Priority", value: entry?.priority || "—" },
      ]};
    }
    if (groupField === "nutrition_order") {
      return { cols: [
        { label: "Diet Type", value: entry?.oral_diet?.diet_type || entry?.diet_type || "—" },
        { label: "Priority", value: entry?.priority || "—" },
        { label: "Intent", value: entry?.intent || "—" },
      ]};
    }
    return { cols: [] };
  };

  const medicationOrderFromSetLine = (line) => {
    const medGroup = fields.find((f) => f.field === "medication_order");
    const emptyGroup = buildInitialValues(medGroup?.structure || []);
    const medFields = ((medGroup?.structure || []).find((f) => f.field === "medication")?.structure) || [];
    const fieldOpts = (name) => {
      const def = medFields.find((f) => f.field === name);
      return Array.isArray(def?.options) && def.options.length ? def.options : null;
    };
    const priorityOpts = ((medGroup?.structure || []).find((f) => f.field === "priority")?.options) || [];
    const formRaw = String(line.dosage_form || line.form || "").trim();
    const form = formRaw ? formRaw.charAt(0).toUpperCase() + formRaw.slice(1).toLowerCase() : "";
    const dose = line.default_quantity_per_dose ?? 1;
    const duration = line.default_days ?? 5;
    const drugName = line.item_name || line.name || "";
    const medication = {
      ...(emptyGroup.medication || {}),
      drug_name: drugName,
      name: drugName,
      item_id: line.item_id != null ? String(line.item_id) : "",
      generic_name: line.generic_name || "",
      route: pickFieldOption(fieldOpts("route"), defaultRouteForForm(form), "Oral"),
      frequency: pickFieldOption(fieldOpts("frequency"), line.default_frequency_code, "Daily"),
      morning: dose,
      afternoon: 0,
      evening: 0,
      night: 0,
      duration,
      duration_unit: pickFieldOption(fieldOpts("duration_unit"), "Day(s)", "Day(s)"),
      instructions: "",
    };
    const qty = totalQuantity(medication);
    medication.total_quantity = qty === null ? "" : qty;
    return {
      ...emptyGroup,
      priority: pickFieldOption(
        priorityOpts,
        formData?.medication_order?.priority,
        "Routine",
      ),
      reason: formData?.medication_order?.reason || emptyGroup.reason || "",
      medication,
    };
  };

  const handleSelectOrderSet = (id) => {
    setSelectedOrderSetId(id);
    if (!id) return;
    setOrderSetExpanding(true);
    expandOrderSet(id)
      .then((lines) => {
        const safeLines = Array.isArray(lines) ? lines : [];
        const entries = safeLines
          .filter((line) => line?.item_name || line?.name || line?.item_id)
          .map(medicationOrderFromSetLine);
        if (entries.length === 0) {
          toast.info("This order set has no medicines.");
          return;
        }
        let added = 0;
        setOrderQueues((prev) => {
          const existingNames = new Set(
            (prev.medication_order || []).map((entry) =>
              String(entry?.medication?.drug_name || entry?.medication?.name || "").trim().toLowerCase(),
            ),
          );
          const fresh = entries.filter((entry) => {
            const name = String(entry?.medication?.drug_name || "").trim().toLowerCase();
            if (!name || existingNames.has(name)) return false;
            existingNames.add(name);
            return true;
          });
          added = fresh.length;
          if (fresh.length === 0) return prev;
          return {
            ...prev,
            medication_order: [...prev.medication_order, ...fresh],
          };
        });
        if (added === 0) {
          toast.info("Those medicines are already in the list.");
        } else {
          toast.success(
            `Added ${added} medicine${added === 1 ? "" : "s"} from the order set.`,
          );
        }
      })
      .catch((err) => {
        toast.error(err.message || "Could not load this order set.");
      })
      .finally(() => setOrderSetExpanding(false));
  };

  const handleOrderAddToActivity = () => {
    const activeTab = ORDER_TABS[activeOrderTab];
    if (!activeTab) return;
    const groupData = formData[activeTab.groupField] || {};

    if (isOrderGroupEmpty(groupData)) {
      toast.error("Please fill in at least one field before adding.");
      return;
    }

    setOrderQueues((prev) => ({
      ...prev,
      [activeTab.groupField]: [...prev[activeTab.groupField], { ...groupData }],
    }));
    // Reset only this tab's group fields
    const groupFieldDef = fields.find((f) => f.field === activeTab.groupField);
    const emptyGroup = buildInitialValues(groupFieldDef?.structure || []);
    setFormData((prev) => ({ ...prev, [activeTab.groupField]: emptyGroup }));
    setFieldErrors({});
  };

  const handleOrderEditQueued = (groupField, index) => {
    const entry = orderQueues[groupField]?.[index];
    if (!entry) return;
    setFormData((prev) => ({ ...prev, [groupField]: { ...entry } }));
    setOrderQueues((prev) => ({
      ...prev,
      [groupField]: prev[groupField].filter((_, i) => i !== index),
    }));
  };

  const handleOrderRemoveQueued = (groupField, index) => {
    setOrderQueues((prev) => ({
      ...prev,
      [groupField]: prev[groupField].filter((_, i) => i !== index),
    }));
  };

  const handleEditQueued = (indexToEdit) => {
    const entry = queuedEntries[indexToEdit];
    if (!entry) return;
    setFormData(entry);
    setEditingEntryIndex(indexToEdit);
    setFieldErrors({});
  };

  const handleRemoveQueued = (indexToRemove) => {
    setQueuedEntries((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    if (editingEntryIndex === indexToRemove) {
      setEditingEntryIndex(null);
      setFormData(createEmptyEntry());
    } else if (editingEntryIndex !== null && indexToRemove < editingEntryIndex) {
      setEditingEntryIndex((prev) => (prev === null ? null : prev - 1));
    }
  };

  const hasCurrentDraftData = !isEmptyValue(stripBillingMetaFromEntry(formData));

  const getSubmitPayload = () => {
    if (activityName === "orders") {
      // Auto-include any filled-in form data that wasn't explicitly queued via ADD TO ACTIVITY
      const medQueue = [...orderQueues.medication_order];
      const testQueue = [...orderQueues.test_order];
      const nutQueue = [...orderQueues.nutrition_order];
      ORDER_TABS.forEach((tab) => {
        const groupData = formData[tab.groupField];
        if (!groupData || isOrderGroupEmpty(groupData)) return;
        if (tab.groupField === "medication_order") medQueue.push({ ...groupData });
        else if (tab.groupField === "test_order") testQueue.push({ ...groupData });
        else if (tab.groupField === "nutrition_order") nutQueue.push({ ...groupData });
      });
      return {
        medication_order: medQueue,
        test_order: testQueue,
        nutrition_order: nutQueue,
      };
    }
    if (!isMultiEntry) return formData;
    const queuedWithEditsApplied =
      editingEntryIndex === null
        ? [...queuedEntries]
        : queuedEntries.map((entry, idx) => (idx === editingEntryIndex ? formData : entry));

    if (hasCurrentDraftData && editingEntryIndex === null) {
      return [...queuedWithEditsApplied, formData];
    }
    return queuedWithEditsApplied;
  };

  const validateCurrentForm = () =>
    applyFieldErrors(validateEntryRequiredFields(formData, fields));

  const validateSubmitPayload = () => {
    if (activityName === "orders") {
      // Use getSubmitPayload which already auto-includes any current draft
      const payload = getSubmitPayload();
      const total =
        payload.medication_order.length +
        payload.test_order.length +
        payload.nutrition_order.length;
      if (total === 0) {
        toast.error("Please fill in at least one order before saving.");
        return false;
      }
      return true;
    }

    const payload = getSubmitPayload();
    const entries = Array.isArray(payload) ? payload : [payload];

    if (
      entries.length === 0 ||
      entries.every((entry) => isEmptyValue(stripBillingMetaFromEntry(entry)))
    ) {
      validateCurrentForm();
      toast.error("Please fill in all required fields.");
      return false;
    }

    for (let index = 0; index < entries.length; index += 1) {
      const entryErrors = validateEntryRequiredFields(entries[index], fields);
      if (Object.keys(entryErrors).length > 0) {
        if (isMultiEntry) {
          toast.error(
            `Entry ${index + 1} is missing required fields. Please edit and complete it.`
          );
        } else {
          toast.error("Please fill in all required fields.");
        }

        const isCurrentFormEntry =
          !isMultiEntry ||
          index === editingEntryIndex ||
          (editingEntryIndex === null && index === queuedEntries.length && hasCurrentDraftData);

        if (isCurrentFormEntry) {
          applyFieldErrors(entryErrors);
        }
        return false;
      }
    }

    setFieldErrors({});
    return true;
  };

  const handleAddToActivityWithValidation = () => {
    if (!validateCurrentForm()) {
      toast.error("Please fill in all required fields before adding.");
      return;
    }
    handleAddToActivity();
  };

  const renderField = (field, sourceData, onFieldChange, pathPrefix = []) => {
    const path = [...pathPrefix, field.field];
    const fieldValue = getNestedValue(sourceData, path);
    const label = toLabel(field.field);
    const pathKey = path.join(".");
    const fieldError = fieldErrors[pathKey] || "";
    const isRequired = isFieldRequired(field);

    if (field.type === "object") {
      // Special layout for medication object: compact rows for frequency+doses and duration+unit
      const DOSE_FIELDS = ["morning", "afternoon", "evening", "night"];
      const DOSE_LABELS = { morning: "M", afternoon: "A", evening: "E", night: "N" };
      const isMedicationObj = field.field === "medication";

      const structure = field.structure || [];
      const freqField = isMedicationObj ? structure.find((f) => f.field === "frequency") : null;
      const doseFields = isMedicationObj ? structure.filter((f) => DOSE_FIELDS.includes(f.field)) : [];
      const durationField = isMedicationObj ? structure.find((f) => f.field === "duration") : null;
      const durationUnitField = isMedicationObj ? structure.find((f) => f.field === "duration_unit") : null;

      return (
        <Grid item xs={12} key={path.join(".")}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {label}
              {isRequired ? " *" : ""}
            </Typography>
            <Grid container spacing={1.5}>
              {isMedicationObj
                ? structure.map((nestedField) => {
                    // Skip dose fields — rendered inside the frequency row
                    if (DOSE_FIELDS.includes(nestedField.field)) return null;
                    // Skip duration_unit — rendered next to duration
                    if (nestedField.field === "duration_unit") return null;

                    // Frequency + M/A/E/N compact row
                    if (nestedField.field === "frequency" && freqField) {
                      return (
                        <Grid item xs={12} key="freq-dose-row">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ minWidth: 130, flexShrink: 0 }}>
                              <FormControl fullWidth size="small" required={isFieldRequired(freqField)}>
                                <InputLabel required={isFieldRequired(freqField)}>Frequency</InputLabel>
                                <Select
                                  label="Frequency"
                                  value={getNestedValue(sourceData, [...path, "frequency"]) ?? ""}
                                  onChange={(e) => onFieldChange([...path, "frequency"], e.target.value)}
                                >
                                  {(freqField.options || []).map((opt) => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            {doseFields.map((df) => (
                              <Box key={df.field} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6b7280", flexShrink: 0 }}>
                                  {DOSE_LABELS[df.field]}
                                </Typography>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={getNestedValue(sourceData, [...path, df.field]) ?? ""}
                                  onChange={(e) => onFieldChange([...path, df.field], e.target.value)}
                                  inputProps={{ min: 0, style: { width: 44, textAlign: "center", padding: "6px 4px" } }}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Grid>
                      );
                    }

                    // Duration + Duration Unit compact row
                    if (nestedField.field === "duration" && durationField && durationUnitField) {
                      return (
                        <Grid item xs={12} key="duration-row">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <TextField
                              size="small"
                              type="number"
                              label="Duration"
                              required={isFieldRequired(durationField)}
                              value={getNestedValue(sourceData, [...path, "duration"]) ?? ""}
                              onChange={(e) => onFieldChange([...path, "duration"], e.target.value)}
                              inputProps={{ min: 0 }}
                              sx={{ flex: 1 }}
                            />
                            <FormControl size="small" required={isFieldRequired(durationUnitField)} sx={{ minWidth: 130 }}>
                              <InputLabel required={isFieldRequired(durationUnitField)}>Unit</InputLabel>
                              <Select
                                label="Unit"
                                value={getNestedValue(sourceData, [...path, "duration_unit"]) ?? ""}
                                onChange={(e) => onFieldChange([...path, "duration_unit"], e.target.value)}
                              >
                                {(durationUnitField.options || []).map((opt) => (
                                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </Grid>
                      );
                    }

                    // Instructions — dropdown (meal-timing options), not free text
                    if (nestedField.field === "instructions") {
                      return (
                        <Grid item xs={12} sm={6} key={[...path, "instructions"].join(".")}>
                          <FormControl fullWidth size="small" required={isFieldRequired(nestedField)}>
                            <InputLabel required={isFieldRequired(nestedField)}>Instructions</InputLabel>
                            <Select
                              label="Instructions"
                              value={getNestedValue(sourceData, [...path, "instructions"]) ?? ""}
                              onChange={(e) => onFieldChange([...path, "instructions"], e.target.value)}
                            >
                              {MEDICATION_INSTRUCTION_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      );
                    }

                    return renderField(nestedField, sourceData, onFieldChange, path);
                  })
                : structure.map((nestedField) =>
                    renderField(nestedField, sourceData, onFieldChange, path)
                  )}
            </Grid>
          </Paper>
        </Grid>
      );
    }

    if (field.type === "group") {
      // For "orders" activity all groups are always visible (no dropdown selection needed).
      // For other activities, only show a group when its condition matches.
      if (field.condition && activityName !== "orders") {
        const condCurrentValue = getNestedValue(sourceData, [field.condition.field]);
        if (condCurrentValue !== field.condition.value) return null;
      }
      return (
        <Grid item xs={12} key={path.join(".")}>
          <Paper
            variant="outlined"
            sx={{ p: 2, mt: 0.5, borderRadius: 2 }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, mb: 1.5, color: "#121959", fontSize: "0.88rem" }}
            >
              {field.label || label}
            </Typography>
            <Grid container spacing={1.5}>
              {(field.structure || []).map((nestedField) =>
                renderField(nestedField, sourceData, onFieldChange, path)
              )}
            </Grid>
          </Paper>
        </Grid>
      );
    }

    if (field.type === "boolean") {
      return (
        <Grid item xs={12} key={path.join(".")}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(fieldValue)}
                onChange={(event) => onFieldChange(path, event.target.checked)}
              />
            }
            label={label}
          />
        </Grid>
      );
    }

    if (field.type === "select") {
      // Hide the order_type selector for "orders" — all groups are shown at once
      if (activityName === "orders" && field.field === "order_type") return null;

      const fieldNameLower = String(field.field || "").toLowerCase();
      const isAppointmentSpecialty = isAppointmentActivity && fieldNameLower === "specialty";
      const isAppointmentDoctor = isAppointmentActivity && fieldNameLower === "doctor";

      if (isAppointmentSpecialty) {
        return (
          <Grid item xs={12} sm={6} key={path.join(".")}>
            <FormControl fullWidth size="small" required={isRequired} error={Boolean(fieldError)}>
              <InputLabel required={isRequired}>{label}</InputLabel>
              <Select
                label={label}
                value={fieldValue ?? ""}
                onChange={(event) => {
                  const selectedDeptName = event.target.value;
                  onFieldChange(path, selectedDeptName);
                  const doctorPath = [...pathPrefix, "doctor"];
                  onFieldChange(doctorPath, "");
                  fetchDoctorsByDepartment(selectedDeptName);
                }}
                endAdornment={loadingDepartments ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
              >
                {departmentOptions.map((dep) => (
                  <MenuItem key={dep.departmentName} value={dep.departmentName}>
                    {dep.departmentName}
                  </MenuItem>
                ))}
              </Select>
              {fieldError ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {fieldError}
                </Typography>
              ) : null}
            </FormControl>
          </Grid>
        );
      }

      if (isAppointmentDoctor) {
        return (
          <Grid item xs={12} sm={6} key={path.join(".")}>
            <FormControl fullWidth size="small" required={isRequired} error={Boolean(fieldError)}>
              <InputLabel required={isRequired}>{label}</InputLabel>
              <Select
                label={label}
                value={fieldValue ?? ""}
                onChange={(event) => onFieldChange(path, event.target.value)}
                disabled={doctorOptions.length === 0 && !loadingDoctors}
                endAdornment={loadingDoctors ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
              >
                {doctorOptions.map((doc) => (
                  <MenuItem key={doc.doctor_id} value={doc.name}>
                    {doc.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldError ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {fieldError}
                </Typography>
              ) : null}
            </FormControl>
          </Grid>
        );
      }

      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <FormControl fullWidth size="small" required={isRequired} error={Boolean(fieldError)}>
            <InputLabel required={isRequired}>{label}</InputLabel>
            <Select
              label={label}
              value={fieldValue ?? ""}
              onChange={(event) => onFieldChange(path, event.target.value)}
            >
              {(field.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {toLabel(option)}
                </MenuItem>
              ))}
            </Select>
            {fieldError ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {fieldError}
              </Typography>
            ) : null}
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "multi-select") {
      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <FormControl fullWidth size="small" required={isRequired} error={Boolean(fieldError)}>
            <InputLabel required={isRequired}>{label}</InputLabel>
            <Select
              multiple
              label={label}
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={(event) => onFieldChange(path, event.target.value)}
              renderValue={(selected) => selected.map((item) => toLabel(item)).join(", ")}
            >
              {(field.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {toLabel(option)}
                </MenuItem>
              ))}
            </Select>
            {fieldError ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {fieldError}
              </Typography>
            ) : null}
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "radio") {
      return (
        <Grid item xs={12} key={path.join(".")}>
          <FormControl required={isRequired} error={Boolean(fieldError)}>
            <FormLabel sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#374151", mb: 0.5 }}>
              {label}{isRequired ? " *" : ""}
            </FormLabel>
            <RadioGroup
              row
              value={fieldValue ?? ""}
              onChange={(e) => onFieldChange(path, e.target.value)}
            >
              {(field.options || []).map((opt) => (
                <FormControlLabel
                  key={opt}
                  value={opt}
                  control={<Radio size="small" sx={{ color: "#21cdc0", "&.Mui-checked": { color: "#21cdc0" } }} />}
                  label={<Typography sx={{ fontSize: "0.83rem" }}>{toLabel(opt)}</Typography>}
                />
              ))}
            </RadioGroup>
            {fieldError && <Typography variant="caption" color="error">{fieldError}</Typography>}
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "datetime") {
      const selected = parseDateValue(fieldValue);
      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <DatePicker
            selected={selected}
            onChange={(date) =>
              onFieldChange(path, date ? formatDateForApi(date, true) : "")
            }
            showTimeSelect
            timeIntervals={15}
            dateFormat="dd-MM-yyyy h:mm aa"
            timeCaption="Time"
            placeholderText="dd-mm-yyyy --:--"
            customInput={
              <DatePickerTextField
                label={label}
                endIcon={<DateTimeFieldEndIcon />}
                required={isRequired}
                error={Boolean(fieldError)}
                helperText={fieldError || " "}
              />
            }
            popperProps={CARE_PLAN_DATE_PICKER_POPPER}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "date") {
      const selected = parseDateValue(fieldValue);
      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <DatePicker
            selected={selected}
            onChange={(date) =>
              onFieldChange(path, date ? formatDateForApi(date, false) : "")
            }
            dateFormat="dd-MM-yyyy"
            placeholderText="dd-mm-yyyy"
            customInput={
              <DatePickerTextField
                label={label}
                endIcon={<CalendarTodayIcon sx={{ fontSize: 20 }} />}
                required={isRequired}
                error={Boolean(fieldError)}
                helperText={fieldError || " "}
              />
            }
            popperProps={CARE_PLAN_DATE_PICKER_POPPER}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "time") {
      const selectedTime = parseTimeFieldValue(fieldValue);
      return (
        <Grid item xs={12} sm={6} key={path.join(".")}>
          <DatePicker
            selected={selectedTime}
            onChange={(date) => onFieldChange(path, date ? formatTimeForApi(date) : "")}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            customInput={
              <DatePickerTextField
                label={label}
                endIcon={<AccessTimeIcon sx={{ fontSize: 20 }} />}
                required={isRequired}
                error={Boolean(fieldError)}
                helperText={fieldError || " "}
              />
            }
            popperProps={CARE_PLAN_DATE_PICKER_POPPER}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "table") {
      const rows = Array.isArray(fieldValue) ? fieldValue : [];
      const columns = field.columns || [];
      const emptyTableRow = () =>
        columns.reduce((acc, col) => ({ ...acc, [col.field]: col.type === "boolean" ? false : "" }), {});

      const handleAddTableRow = () =>
        onFieldChange(path, [...rows, emptyTableRow()]);

      const handleRemoveTableRow = (rowIdx) =>
        onFieldChange(path, rows.filter((_, i) => i !== rowIdx));

      const handleCellChange = (rowIdx, colField, value) =>
        onFieldChange(
          path,
          rows.map((row, i) => (i === rowIdx ? { ...row, [colField]: value } : row))
        );

      // [LAB_TEST] When a medical service is picked in a dynamic-select cell
      // (Orders → Investigation), stash its id/code alongside the display text
      // so the add_patient_lab_tests/ payload can carry medical_service_id +
      // service code — not just the name string. Also keep charge-capture flags
      // so amount/qty can be locked when the modal exposes those fields.
      const handleCellSelectService = (rowIdx, colField, value, item) =>
        onFieldChange(
          path,
          rows.map((row, i) => {
            if (i !== rowIdx) return row;
            const meta = { ...(row.__service_meta || {}) };
            const serviceId = item?.medical_service_id ?? item?.id ?? "";
            const code = String(
              item?.service_code ??
                item?.code ??
                item?.conceptId ??
                item?.concept_id ??
                item?.snomed_code ??
                "",
            );
            if (serviceId || code || item?.name || item?.term) {
              meta[colField] = {
                name: item?.name || item?.term || value,
                medical_service_id: String(serviceId),
                code,
                rate: item?.rate ?? item?.fee ?? null,
                charge_modifiable: item?.chargeModifiable === true || item?.charge_modifiable === true,
                quantity_modifiable: item?.quantityModifiable !== false && item?.quantity_modifiable !== false,
              };
            } else {
              delete meta[colField];
            }
            return { ...row, [colField]: value, __service_meta: meta };
          })
        );

      return (
        <Grid item xs={12} key={path.join(".")}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#121959" }}>
                {label}{isRequired ? " *" : ""}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddTableRow}
                sx={{ fontSize: "0.73rem", textTransform: "none", color: "#21cdc0" }}
              >
                Add Row
              </Button>
            </Box>

            {rows.length > 0 ? (
              <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5fffe" }}>
                      {columns.map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#374151", py: 0.8, whiteSpace: "nowrap" }}
                        >
                          {toLabel(col.field)}
                          {col.required && (
                            <Typography component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Typography>
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={{ width: 36 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, rowIdx) => (
                      <TableRow key={rowIdx}>
                        {columns.map((col) => {
                          // Boolean column → compact checkbox
                          if (col.type === "boolean") {
                            return (
                              <TableCell key={col.field} sx={{ py: 0.4, px: 1, textAlign: "center" }}>
                                <Checkbox
                                  size="small"
                                  checked={Boolean(row[col.field])}
                                  onChange={(e) => handleCellChange(rowIdx, col.field, e.target.checked)}
                                  sx={{ p: 0, color: "#21cdc0", "&.Mui-checked": { color: "#21cdc0" } }}
                                />
                              </TableCell>
                            );
                          }
                          // Select column → dropdown with static options
                          if (col.type === "select") {
                            return (
                              <TableCell key={col.field} sx={{ py: 0.4, px: 1, minWidth: 160 }}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    displayEmpty
                                    value={row[col.field] ?? ""}
                                    onChange={(e) => handleCellChange(rowIdx, col.field, e.target.value)}
                                    sx={{ fontSize: "0.78rem" }}
                                  >
                                    <MenuItem value="" disabled sx={{ fontSize: "0.78rem" }}>
                                      <em>Select…</em>
                                    </MenuItem>
                                    {(col.options || []).map((opt) => (
                                      <MenuItem key={opt} value={opt} sx={{ fontSize: "0.78rem" }}>
                                        {toLabel(opt)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                            );
                          }
                          // Dynamic-select column → freeSolo Autocomplete (searches medical services)
                          if (col.type === "dynamic-select") {
                            const dynKey = `${path.join(".")}.${rowIdx}.${col.field}`;
                            // Backend can return multiple records with the same display name
                            // (e.g. duplicate charge-master rows) — dedupe so one match doesn't
                            // eat every slot in the suggestion list.
                            const dynOpts = Array.from(
                              new Set((chargeOptionsByKey[dynKey] || []).map((i) => i.name).filter(Boolean))
                            );
                            const isLoading = Boolean(chargeLoadingByKey[dynKey]);
                            // [ORDERS_INVESTIGATION] Category (Laboratory/Radiology/Other) decides
                            // which service_type the test-name search hits below. Typing stays
                            // enabled even with no category picked, but the search is withheld
                            // and a red helper text prompts for the category instead. Only groups
                            // whose schema actually has a "category" field are gated.
                            const groupData = getNestedValue(sourceData, pathPrefix) || {};
                            const hasCategoryField = Object.prototype.hasOwnProperty.call(groupData, "category");
                            const categoryRequired = hasCategoryField && !groupData.category;
                            return (
                              <TableCell key={col.field} sx={{ py: 0.4, px: 1, minWidth: 180 }}>
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  fullWidth
                                  options={dynOpts}
                                  loading={isLoading}
                                  value={row[col.field] ?? ""}
                                  onInputChange={(_, val) => {
                                    handleCellChange(rowIdx, col.field, val);
                                    if (categoryRequired) return;
                                    if (!val || val.length < 2) return;
                                    const categoryLower = String(groupData.category || "").toLowerCase();
                                    // Patient-aware charge-capture search: admission_id + group_code
                                    // (Laboratory → LABORATORY, Radiology → RADIOLOGY). "Other" omits
                                    // group_code so the list stays bed-scoped but not group-scoped.
                                    const groupCode = investigationCategoryToGroupCode(categoryLower);
                                    setChargeLoadingByKey((p) => ({ ...p, [dynKey]: true }));
                                    searchChargeCaptureServices(val, {
                                      limit: 50,
                                      admissionId,
                                      groupCode,
                                    })
                                      .then((items) => {
                                        setChargeOptionsByKey((p) => ({
                                          ...p,
                                          [dynKey]: items.map((i) => ({
                                            ...i,
                                            name: i.name || i.term || i.service_name || i.label || "",
                                            medical_service_id: i.medical_service_id ?? i.id,
                                            service_code: i.service_code ?? i.code,
                                            code: i.service_code ?? i.code,
                                          })),
                                        }));
                                      })
                                      .catch(() => {})
                                      .finally(() => setChargeLoadingByKey((p) => ({ ...p, [dynKey]: false })));
                                  }}
                                  onChange={(_, val) => {
                                    const selectedName = typeof val === "string" ? val : (val || "");
                                    const picked = (chargeOptionsByKey[dynKey] || []).find(
                                      (i) => (i.name || i.term || "") === selectedName
                                    );
                                    handleCellSelectService(rowIdx, col.field, selectedName, picked);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      required={col.required}
                                      placeholder={`Search ${toLabel(col.field)}…`}
                                      error={categoryRequired}
                                      helperText={categoryRequired ? "Select category first" : ""}
                                      onBlur={(e) => {
                                        params.onBlur?.(e);
                                        // [LAB_TEST] freeSolo lets a doctor type the full name and click away
                                        // without ever choosing the dropdown row, which skips onChange entirely
                                        // and leaves __service_meta (medical_service_id/code) unset. If the typed
                                        // text exactly matches a fetched result, attach its metadata here too.
                                        const currentVal = row[col.field] || "";
                                        if (!currentVal || row.__service_meta?.[col.field]) return;
                                        const matched = (chargeOptionsByKey[dynKey] || []).find(
                                          (i) => (i.name || i.term || "") === currentVal
                                        );
                                        if (matched) handleCellSelectService(rowIdx, col.field, currentVal, matched);
                                      }}
                                      InputProps={{ ...params.InputProps, endAdornment: isLoading ? <CircularProgress size={14} /> : params.InputProps.endAdornment }}
                                      sx={{
                                        "& .MuiInputBase-input": { fontSize: "0.78rem", py: "5px" },
                                        "& .MuiFormHelperText-root": { fontSize: "0.68rem", color: "#ef4444", mx: 0, mt: 0.3 },
                                      }}
                                    />
                                  )}
                                />
                              </TableCell>
                            );
                          }
                          // Default: text / number / date — lock qty/amount when the
                          // selected charge-capture service says they aren't modifiable.
                          const serviceMeta =
                            row.__service_meta &&
                            typeof row.__service_meta === "object"
                              ? Object.values(row.__service_meta)[0]
                              : null;
                          const fieldKey = String(col.field || "").toLowerCase();
                          const isQtyField = ["qty", "quantity"].includes(fieldKey);
                          const isAmountField = ["amount", "rate", "fee", "charge", "unit_price"].includes(fieldKey);
                          const lockQty =
                            Boolean(serviceMeta) &&
                            isQtyField &&
                            serviceMeta.quantity_modifiable === false;
                          const lockAmount =
                            Boolean(serviceMeta) &&
                            isAmountField &&
                            serviceMeta.charge_modifiable !== true;
                          const locked = lockQty || lockAmount;
                          return (
                            <TableCell key={col.field} sx={{ py: 0.4, px: 1 }}>
                              <TextField
                                size="small"
                                fullWidth
                                type={col.type === "date" ? "date" : col.type === "number" ? "number" : "text"}
                                value={
                                  isAmountField && (row[col.field] == null || row[col.field] === "") && serviceMeta?.rate != null
                                    ? serviceMeta.rate
                                    : (row[col.field] ?? "")
                                }
                                onChange={(e) => handleCellChange(rowIdx, col.field, e.target.value)}
                                required={col.required}
                                disabled={locked}
                                InputProps={locked ? { readOnly: true } : undefined}
                                InputLabelProps={col.type === "date" ? { shrink: true } : undefined}
                                sx={{
                                  "& .MuiInputBase-input": {
                                    fontSize: "0.78rem",
                                    py: "5px",
                                    ...(locked ? { backgroundColor: "#F9FAFB" } : {}),
                                  },
                                }}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell sx={{ py: 0.4, px: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTableRow(rowIdx)}
                            sx={{ color: "#ef4444", "&:hover": { backgroundColor: "#fef2f2" } }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : (
              <Box
                onClick={handleAddTableRow}
                sx={{
                  border: "1px dashed #d1d5db", borderRadius: 1, p: 1.5,
                  textAlign: "center", cursor: "pointer",
                  "&:hover": { borderColor: "#21cdc0", backgroundColor: "#f5fffe" },
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Click "+ Add Row" to add {label.toLowerCase()} entries
                </Typography>
              </Box>
            )}
            {fieldError && (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                {fieldError}
              </Typography>
            )}
          </Box>
        </Grid>
      );
    }

    const isMultiline = field.type === "textarea";
    const inputType = field.type === "number" ? "number" : "text";
    // [BILLING] Charge-master autocomplete replaces plain TextField for Medication/Test fields.
    const chargeSearchConfig = getChargeSearchConfig(field, activityName, pathPrefix);

    if (chargeSearchConfig
      // && !isMultiline
      && inputType === "text") {
      return (
        <CarePlanServiceAutocomplete
          key={path.join(".")}
          search={serviceSearch}
          chargeSearchConfig={chargeSearchConfig}
          path={path}
          label={label}
          fieldValue={fieldValue}
          isRequired={isRequired}
          fieldError={fieldError}
          onFieldChange={onFieldChange}
        />
      );
    }

    return (
      <Grid item xs={12} sm={isMultiline ? 12 : 6} key={path.join(".")}>
        <TextField
          fullWidth
          size="small"
          label={label}
          type={inputType}
          value={fieldValue ?? ""}
          multiline={isMultiline}
          rows={isMultiline ? 3 : undefined}
          required={isRequired}
          error={Boolean(fieldError)}
          helperText={fieldError || " "}
          onChange={(event) => onFieldChange(path, event.target.value)}
        />
      </Grid>
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateSubmitPayload()) return;

    setIsSubmitting(true);
    try {
      const payload = getSubmitPayload();
      // [BILLING] Keep billing metadata for postBillingCharges; strip it before care_plans/ save.
      // [LAB_TEST] Likewise keep __service_meta for postPatientLabTests, then strip it.
      const billingEntries = Array.isArray(payload) ? payload : [payload];
      const carePlanPayload = stripServiceMetaDeep(stripBillingMetaFromPayload(payload));
      const result = await onSubmit(carePlanPayload);
      if (result !== false) {
        // [BILLING] Billing runs after care plan save; failure is logged but does not block close.
        try {
          await postBillingCharges(billingEntries);
        } catch (billingError) {
          console.error("Failed to post billing charges:", billingError);
        }
        // [LAB_TEST] Lab test order runs after care plan + billing (Test + Orders → Investigation).
        try {
          await postPatientLabTests(billingEntries);
        } catch (labTestError) {
          console.error("Failed to post patient lab tests:", labTestError);
          toast.warn("Care plan saved, but lab order could not be created. Please retry from Orders.");
        }
        // [PHARMACY_RX] Prescription runs after care plan + billing + lab test order
        // (Medication + Orders → Medication) so it reaches the pharmacy like Sign & Send does.
        try {
          await postIpdPrescription(billingEntries);
        } catch (prescriptionError) {
          console.error("Failed to post IPD prescription:", prescriptionError);
          toast.warn("Care plan saved, but prescription could not be sent to pharmacy. Please retry from Orders.");
        }
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 600 }}>
        {structureDef?.name || "Care Plan Activity"}
      </DialogTitle>
      <form onSubmit={handleSubmit} noValidate>
        <DialogContent dividers sx={{ maxHeight: "70vh" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {isMultiEntry && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Added to activity: {queuedEntries.length}
                </Typography>
              </Box>
            )}
            {activityName === "orders" ? (() => {
              const activeTab = ORDER_TABS[activeOrderTab];
              const groupField = fields.find((f) => f.field === activeTab?.groupField);
              const queuedCount = (orderQueues[activeTab?.groupField] || []).length;
              return (
                <Box>
                  {/* Tab bar — segmented control */}
                  <Box
                    sx={{
                      display: "flex",
                      backgroundColor: "#f1f3f5",
                      borderRadius: "10px",
                      p: "4px",
                      mb: 2,
                      gap: "4px",
                    }}
                  >
                    {ORDER_TABS.map((t, i) => {
                      const isActive = activeOrderTab === i;
                      const tabQueued = (orderQueues[t.groupField] || []).length;
                      return (
                        <Box
                          key={i}
                          onClick={() => setActiveOrderTab(i)}
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.8,
                            py: "9px",
                            px: 1,
                            borderRadius: "7px",
                            cursor: "pointer",
                            backgroundColor: isActive ? "#ffffff" : "transparent",
                            boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                            transition: "all 0.15s",
                            position: "relative",
                            "&:hover": {
                              backgroundColor: isActive ? "#ffffff" : "rgba(255,255,255,0.5)",
                            },
                          }}
                        >
                          <Box sx={{ color: isActive ? "#21cdc0" : "#6b7280", display: "flex", fontSize: 18 }}>
                            {t.tabIcon}
                          </Box>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: isActive ? 700 : 500, color: isActive ? "#0e3f66" : "#6b7280", whiteSpace: "nowrap" }}>
                            {t.label}
                          </Typography>
                          {tabQueued > 0 && (
                            <Box sx={{
                              backgroundColor: "#21cdc0", color: "#fff",
                              borderRadius: "10px", fontSize: "0.65rem", fontWeight: 700,
                              px: 0.7, lineHeight: "18px", minWidth: 18, textAlign: "center",
                            }}>
                              {tabQueued}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Section header */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 0.5, borderBottom: "1px solid #f0f4f8" }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: 2,
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e4e9ef",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      {activeTab.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#121959" }}>
                        {activeTab.title}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                        {activeTab.subtitle}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${queuedCount} added`}
                      size="small"
                      sx={{
                        fontSize: "0.72rem", fontWeight: 600,
                        backgroundColor: queuedCount > 0 ? "#EBFAF9" : "#f0f4f8",
                        color: queuedCount > 0 ? "#0a7a73" : "#6b7280",
                      }}
                    />
                  </Box>

                  {/* Tab content */}
                  <Box sx={{ pt: 2 }}>
                    {/* Order Set — medication (same sets as Order Management) */}
                    {activeTab.groupField === "medication_order" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                        {/* [PHARMACY_RX] Only rendered when the hospital has more
                            than one pharmacy store — otherwise the single store is
                            already selected and there's nothing to ask. */}
                        <PharmacyStoreSelect
                          stores={pharmacyStore.stores}
                          storeId={pharmacyStore.storeId}
                          onChange={pharmacyStore.setStoreId}
                          loading={pharmacyStore.loading}
                          error={pharmacyStore.error}
                          showPicker={pharmacyStore.showPicker}
                          width={220}
                        />
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", whiteSpace: "nowrap", flexShrink: 0 }}>
                          Order Set:
                        </Typography>
                        <FormControl
                          size="small"
                          sx={{
                            minWidth: 260,
                            flex: 1,
                            maxWidth: 380,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { borderColor: "#21cdc0", borderWidth: 1.5 },
                              "&:hover fieldset": { borderColor: "#0e9e94" },
                              "&.Mui-focused fieldset": { borderColor: "#0E3F66", borderWidth: 2 },
                            },
                          }}
                        >
                          <Select
                            displayEmpty
                            value={selectedOrderSetId}
                            onChange={(e) => handleSelectOrderSet(e.target.value)}
                            disabled={orderSetExpanding}
                            renderValue={(v) => {
                              if (!v) {
                                return (
                                  <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                                    Order Set — pick a saved set…
                                  </Typography>
                                );
                              }
                              const selected = orderSets.find(
                                (s) => String(getOrderSetId(s)) === String(v),
                              );
                              return (
                                <Typography sx={{ fontSize: "0.85rem" }}>
                                  {selected
                                    ? `${selected.set_name} (${selected.set_code})`
                                    : v}
                                </Typography>
                              );
                            }}
                            endAdornment={
                              orderSetExpanding ? (
                                <InputAdornment position="end" sx={{ mr: 2 }}>
                                  <CircularProgress size={14} />
                                </InputAdornment>
                              ) : null
                            }
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {orderSetsStatus === "loading" && (
                              <MenuItem disabled>Loading order sets…</MenuItem>
                            )}
                            {orderSetsStatus === "error" && (
                              <MenuItem disabled sx={{ color: "#991B1B" }}>
                                {orderSetsError || "Could not load order sets."}
                              </MenuItem>
                            )}
                            {orderSetsStatus === "loaded" && orderSets.length === 0 && (
                              <MenuItem disabled>No order sets yet for this hospital.</MenuItem>
                            )}
                            {orderSets.map((set) => (
                              <MenuItem key={getOrderSetId(set)} value={getOrderSetId(set)}>
                                {set.set_name}{" "}
                                <Typography
                                  component="span"
                                  sx={{ ml: 0.8, fontSize: "0.72rem", color: "#9CA3AF" }}
                                >
                                  {set.set_code}
                                  {set.specialty ? ` · ${set.specialty}` : ""}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                    {/* Reuse previous — investigation */}
                    {activeTab.groupField === "test_order" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", whiteSpace: "nowrap", flexShrink: 0 }}>
                          Reuse previous:
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={prevTests}
                          getOptionLabel={(opt) => opt.label || ""}
                          loading={prevMedLoading}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { borderColor: "#21cdc0", borderWidth: 1.5 },
                              "&:hover fieldset": { borderColor: "#0e9e94" },
                              "&.Mui-focused fieldset": { borderColor: "#0E3F66", borderWidth: 2 },
                            },
                          }}
                          onOpen={fetchPrevMedications}
                          onChange={(_, selected) => {
                            if (!selected) return;
                            setFormData((prev) => ({
                              ...prev,
                              test_order: {
                                ...(prev.test_order || {}),
                                ...selected.data,
                              },
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select investigation…"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: prevMedLoading
                                  ? <CircularProgress size={14} />
                                  : params.InputProps.endAdornment,
                              }}
                            />
                          )}
                        />
                      </Box>
                    )}
                    {/* Reuse previous — nutrition */}
                    {activeTab.groupField === "nutrition_order" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", whiteSpace: "nowrap", flexShrink: 0 }}>
                          Reuse previous:
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={prevNutrition}
                          getOptionLabel={(opt) => opt.label || ""}
                          loading={prevMedLoading}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { borderColor: "#21cdc0", borderWidth: 1.5 },
                              "&:hover fieldset": { borderColor: "#0e9e94" },
                              "&.Mui-focused fieldset": { borderColor: "#0E3F66", borderWidth: 2 },
                            },
                          }}
                          onOpen={fetchPrevMedications}
                          onChange={(_, selected) => {
                            if (!selected) return;
                            setFormData((prev) => ({
                              ...prev,
                              nutrition_order: {
                                ...(prev.nutrition_order || {}),
                                ...selected.data,
                              },
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select nutrition order…"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: prevMedLoading
                                  ? <CircularProgress size={14} />
                                  : params.InputProps.endAdornment,
                              }}
                            />
                          )}
                        />
                      </Box>
                    )}
                    <Grid container spacing={1.5}>
                      {groupField
                        ? (groupField.structure || []).map((f) =>
                            renderField(f, formData, handleChange, [activeTab.groupField])
                          )
                        : null}
                    </Grid>
                  </Box>

                  {/* Queued entries for this tab */}
                  {queuedCount > 0 && (
                    <Box sx={{ mt: 2, borderTop: "1px solid #e4e9ef", pt: 1.5 }}>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#374151", mb: 1 }}>
                        Added Entries ({queuedCount})
                      </Typography>
                      {/* Column header row */}
                      {(() => {
                        const { cols } = getOrderEntrySummary(activeTab.groupField, {});
                        if (!cols.length) return null;
                        return (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, mb: 0.5 }}>
                            <Typography sx={{ fontSize: "0.67rem", fontWeight: 700, color: "#9ca3af", width: 20, flexShrink: 0 }}>#</Typography>
                            {cols.map((col) => (
                              <Typography key={col.label} sx={{ fontSize: "0.67rem", fontWeight: 700, color: "#9ca3af", flex: 1 }}>
                                {col.label.toUpperCase()}
                              </Typography>
                            ))}
                            <Box sx={{ width: 64, flexShrink: 0 }} />
                          </Box>
                        );
                      })()}

                      {/* Entry rows */}
                      {(orderQueues[activeTab.groupField] || []).map((entry, idx) => {
                        const { cols } = getOrderEntrySummary(activeTab.groupField, entry);
                        return (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex", alignItems: "center", gap: 1,
                              px: 1.5, py: 0.6, mb: 0.5, borderRadius: 1,
                              backgroundColor: "#f8fafc", border: "1px solid #e4e9ef",
                            }}
                          >
                            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#9ca3af", width: 20, flexShrink: 0 }}>
                              {idx + 1}
                            </Typography>
                            {cols.map((col) => (
                              <Typography
                                key={col.label}
                                title={String(col.value || "")}
                                sx={{ fontSize: "0.78rem", color: col.value && col.value !== "—" ? "#1e293b" : "#d1d5db", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              >
                                {col.value || "—"}
                              </Typography>
                            ))}
                            <Box sx={{ display: "flex", gap: 0, flexShrink: 0 }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOrderEditQueued(activeTab.groupField, idx)}
                                  sx={{ color: "#21cdc0", p: 0.3, "&:hover": { backgroundColor: "#EBFAF9" } }}
                                >
                                  <EditOutlinedIcon sx={{ fontSize: "0.9rem" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOrderRemoveQueued(activeTab.groupField, idx)}
                                  sx={{ color: "#ef4444", p: 0.3, "&:hover": { backgroundColor: "#fef2f2" } }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: "0.9rem" }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })() : (
              <Grid container spacing={1.5}>
                {fields.map((field) => renderField(field, formData, handleChange))}
              </Grid>
            )}
            {isMultiEntry && queuedEntries.length > 0 && (
              <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Added Activities
                </Typography>
                {activityTableConfig ? (
                  <Box sx={{ overflowX: "auto" }}>
                    <Box
                      sx={{
                        minWidth: 560,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: `repeat(${activityTableConfig.columns.length}, minmax(120px, 1fr)) 140px`,
                          columnGap: 1,
                          px: 1,
                        }}
                      >
                        {activityTableConfig.columns.map((col) => (
                          <Box key={`head-${col.key}`} sx={{ py: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {col.label}
                            </Typography>
                          </Box>
                        ))}
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Action
                          </Typography>
                        </Box>
                      </Box>

                      {queuedEntries.map((entry, index) => {
                        const rowData = activityTableConfig.getRow(entry, index);
                        return (
                          <Box
                            key={`row-${index}`}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: `repeat(${activityTableConfig.columns.length}, minmax(120px, 1fr)) 140px`,
                              columnGap: 1,
                              alignItems: "center",
                              px: 1,
                              py: 1,
                              borderRadius: 1,
                              backgroundColor: "grey.50",
                            }}
                          >
                            {activityTableConfig.columns.map((col) => (
                              <Box key={`row-${index}-${col.key}`} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" noWrap title={displayOrDash(rowData[col.key])}>
                                  {displayOrDash(rowData[col.key])}
                                </Typography>
                              </Box>
                            ))}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Chip
                                label="Edit"
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={() => handleEditQueued(index)}
                              />
                              <Chip
                                label="Remove"
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => handleRemoveQueued(index)}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {queuedEntries.map((entry, index) => (
                      <Box
                        key={`queued-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          px: 1.25,
                          py: 0.75,
                        }}
                      >
                        <Typography variant="body2">
                          {getEntryDisplayLabel(entry, structureDef?.name)}
                        </Typography>
                        <Chip
                          label="Remove"
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => handleRemoveQueued(index)}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {(isMultiEntry || activityName === "orders") && (
            <Button
              variant="outlined"
              onClick={activityName === "orders" ? handleOrderAddToActivity : handleAddToActivityWithValidation}
              disabled={isSubmitting}
            >
              {editingEntryIndex !== null ? "Update Activity" : "Add To Activity"}
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            Save Activity
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CarePlanActivityModal;
